import { useMemo, useState } from "react";
import { X } from "lucide-react";

export function EditClientModal({ client, onClose, onUpdate, onSuccess }) {
  const clientMongoId = client?._id || client?.id;

  // Backward compatibility:
  // If old schema had `contact`, try to guess if it's email or phone.
  const legacyContact = (client?.contact || "").trim();
  const legacyLooksEmail =
    legacyContact && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(legacyContact);
  const legacyLooksPhone = legacyContact && /^[0-9]+$/.test(legacyContact);

  const [formData, setFormData] = useState({
    client_name: client?.client_name || "",
    picName: (client?.picName || "").trim(),
    picTitle: (client?.picTitle || "").trim(),
    // Email is REQUIRED now. If missing in new schema, fall back to legacy email if it looks like email.
    email: (client?.email || "").trim() || (legacyLooksEmail ? legacyContact : ""),
    // Phone is optional. If missing in new schema, fall back to legacy phone if it looks like phone.
    phone: (client?.phone || "").trim() || (legacyLooksPhone ? legacyContact : ""),
    address: client?.address || "",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const nameTrim = (formData.client_name || "").trim();
  const picNameTrim = (formData.picName || "").trim();
  const picTitleTrim = (formData.picTitle || "").trim();
  const emailTrim = (formData.email || "").trim();
  const phoneTrim = (formData.phone || "").trim();
  const addressLen = (formData.address || "").length;

  // Email is REQUIRED and must be valid
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrim);

  // Phone stays optional, but if filled must be digits only
  const phoneValid = !phoneTrim || /^[0-9]+$/.test(phoneTrim);

  // Allowed chars: A-Z a-z, spaces, hyphen, dot, comma
  const picNameValid = !picNameTrim || /^[A-Za-z\s\-.,]+$/.test(picNameTrim);

  const addressValid = addressLen <= 500;

  const canSubmit = useMemo(() => {
    return (
      !!clientMongoId &&
      !!nameTrim &&
      !!emailTrim && // required
      emailValid &&
      phoneValid &&
      picNameValid &&
      addressValid &&
      !loading
    );
  }, [
    clientMongoId,
    nameTrim,
    emailTrim,
    emailValid,
    phoneValid,
    picNameValid,
    addressValid,
    loading,
  ]);

  const setField = (key) => (e) => {
    const value = e.target.value;
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  // Hard-block non-digits: whatever user types/pastes, we keep digits only.
  const setPhoneDigitsOnly = (e) => {
    const raw = e.target.value ?? "";
    const digitsOnly = String(raw).replace(/\D+/g, "");
    setFormData((prev) => ({ ...prev, phone: digitsOnly }));
  };

  // PIC name: keep only allowed characters while typing
  const onPicNameChange = (e) => {
    const raw = e.target.value || "";
    const cleaned = raw.replace(/[^A-Za-z\s\-.,]+/g, "");
    setFormData((prev) => ({ ...prev, picName: cleaned }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!clientMongoId) {
      setError("Klien tidak valid (missing id).");
      return;
    }

    const payload = {
      client_name: nameTrim,
      picName: picNameTrim || "",
      picTitle: picTitleTrim || "",
      email: (emailTrim || "").toLowerCase(), // required
      phone: phoneTrim || "", // optional
      address: (formData.address || "").trim() || "",
    };

    if (!payload.client_name) {
      setError("Nama Klien wajib diisi");
      return;
    }

    if (!payload.email) {
      setError("Email wajib diisi.");
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(payload.email)) {
      setError("Format email tidak valid.");
      return;
    }

    // Phone optional, but if filled must be digits
    if (payload.phone && !/^[0-9]+$/.test(payload.phone)) {
      setError("Nomor telepon hanya boleh angka.");
      return;
    }

    if (payload.picName && !/^[A-Za-z\s\-.,]+$/.test(payload.picName)) {
      setError("PIC Name hanya boleh huruf, spasi, '-', '.', dan ','.");
      return;
    }

    if (payload.address.length > 500) {
      setError("Alamat maksimal 500 karakter.");
      return;
    }

    if (typeof onUpdate !== "function") {
      setError("onUpdate belum di-wire dari parent.");
      return;
    }

    try {
      setLoading(true);

      const updated = await onUpdate(clientMongoId, payload);

      onSuccess?.(updated);
      onClose?.();
    } catch (err) {
      const msg =
        err?.message || err?.response?.data?.message || "Gagal mengupdate klien";
      setError(msg);
      console.error("Error updating klien:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 p-4">
      {/* Backdrop */}
      <button
        type="button"
        onClick={onClose}
        className="absolute inset-0 bg-black/50"
        aria-label="Close modal"
      />

      {/* Modal */}
      <div className="relative mx-auto w-full max-w-md max-h-[90vh] overflow-y-auto rounded-2xl bg-white shadow-xl">
        <div className="sticky top-0 z-10 bg-white/95 backdrop-blur border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">Edit Klien</h2>

          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center justify-center rounded-lg p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            aria-label="Close"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error ? (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
              {error}
            </div>
          ) : null}

          {/* Client ID (read-only) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Klien ID
            </label>
            <input
              type="text"
              value={client?.client_id || "-"}
              disabled
              className="w-full px-3 py-2 border border-gray-200 rounded-xl bg-gray-50 text-gray-700 cursor-not-allowed"
            />
            <p className="text-xs text-gray-500 mt-1">
              ID dibuat otomatis oleh sistem dan tidak dapat diubah.
            </p>
          </div>

          {/* Nama Client */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nama Klien <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.client_name}
              onChange={setField("client_name")}
              className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Nama lengkap klien"
              required
              autoFocus
            />
          </div>

          {/* PIC Name (optional) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              PIC Name
            </label>
            <input
              type="text"
              value={formData.picName}
              onChange={onPicNameChange}
              className={`w-full px-3 py-2 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                !picNameValid ? "border-red-300" : "border-gray-300"
              }`}
              placeholder="Nama PIC (opsional)"
            />
            {!picNameValid ? (
              <p className="text-xs text-red-600 mt-1">
                Hanya boleh huruf, spasi, '-', '.', dan ','.
              </p>
            ) : (
              <p className="text-xs text-gray-500 mt-1">
                Opsional. Karakter yang boleh: huruf, spasi, -, ., ,
              </p>
            )}
          </div>

          {/* PIC Title (optional) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              PIC Title
            </label>
            <input
              type="text"
              value={formData.picTitle}
              onChange={setField("picTitle")}
              className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Jabatan PIC (opsional)"
            />
          </div>

          {/* Email (REQUIRED) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={setField("email")}
              required
              className={`w-full px-3 py-2 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                !emailValid ? "border-red-300" : "border-gray-300"
              }`}
              placeholder="nama@perusahaan.com"
            />
            {!emailValid ? (
              <p className="text-xs text-red-600 mt-1">
                Email wajib diisi dan harus valid.
              </p>
            ) : (
              <p className="text-xs text-gray-500 mt-1">
                Pastikan email aktif untuk komunikasi.
              </p>
            )}
          </div>

          {/* Phone (optional, digits only) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nomor Telepon
            </label>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete="tel"
              value={formData.phone}
              onChange={setPhoneDigitsOnly}
              className={`w-full px-3 py-2 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                !phoneValid ? "border-red-300" : "border-gray-300"
              }`}
              placeholder="081234567890"
            />
            {!phoneValid ? (
              <p className="text-xs text-red-600 mt-1">
                Nomor telepon hanya boleh angka.
              </p>
            ) : (
              <p className="text-xs text-gray-500 mt-1">
                Opsional. Jika diisi, hanya angka.
              </p>
            )}
          </div>

          {/* Address (max 500) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Alamat{" "}
              <span className="text-xs text-gray-500">(maks 500 karakter)</span>
            </label>
            <textarea
              value={formData.address}
              onChange={(e) => {
                const v = e.target.value || "";
                setFormData((prev) => ({ ...prev, address: v.slice(0, 500) }));
              }}
              maxLength={500}
              className={`w-full px-3 py-2 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none ${
                !addressValid ? "border-red-300" : "border-gray-300"
              }`}
              rows={3}
              placeholder="Alamat lengkap klien"
            />
            <p className="text-xs text-gray-500 mt-1">
              {Math.min(addressLen, 500)}/500
            </p>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading}
            >
              Batal
            </button>

            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={!canSubmit}
              title={
                !emailTrim
                  ? "Email wajib diisi"
                  : !emailValid
                  ? "Perbaiki format email"
                  : !phoneValid
                  ? "Nomor telepon hanya boleh angka"
                  : !picNameValid
                  ? "PIC Name hanya boleh huruf, spasi, '-', '.', dan ','"
                  : !addressValid
                  ? "Alamat maksimal 500 karakter"
                  : ""
              }
            >
              {loading ? "Menyimpan..." : "Simpan Perubahan"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
